import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  createUser,
  listUsers,
  resendSetupEmail,
  type AdminUser,
  type AdminUserRole,
  updateUserStatus,
} from "api/users";

interface CreateFormState {
  username: string;
  name: string;
  email: string;
  role: AdminUserRole;
}

const INITIAL_CREATE_FORM: CreateFormState = {
  username: "",
  name: "",
  email: "",
  role: "STAFF",
};

function formatDate(value?: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

export default function UsersManagementView(): JSX.Element {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState<CreateFormState>(INITIAL_CREATE_FORM);
  const [creating, setCreating] = useState(false);
  const [busyIds, setBusyIds] = useState<Record<string, boolean>>({});

  const sortedUsers = useMemo(
    () =>
      [...users].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [users]
  );

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const rows = await listUsers();
      setUsers(rows);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load users";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creating) return;
    if (!form.username.trim() || !form.name.trim() || !form.email.trim()) {
      toast.error("Username, name, and email are required.");
      return;
    }
    try {
      setCreating(true);
      const message = await createUser({
        username: form.username.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
      });
      toast.success(message);
      setForm(INITIAL_CREATE_FORM);
      await loadUsers();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to create user";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const withUserBusy = async (userId: string, action: () => Promise<string>) => {
    setBusyIds((prev) => ({ ...prev, [userId]: true }));
    try {
      const message = await action();
      toast.success(message);
      await loadUsers();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Action failed";
      toast.error(msg);
    } finally {
      setBusyIds((prev) => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <DashboardLayout headerTitle="User Management">
      <main className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-5">
          <section className="rounded-lg border border-[#EEEEEE] bg-white p-4 sm:p-6">
            <h1 className="text-[20px] font-bold text-[#222]">User Management</h1>
            <p className="mt-1 text-[13px] text-[#6D6D6D]">
              Create internal users and manage account access.
            </p>

            <form
              onSubmit={(e) => void onCreate(e)}
              className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5"
            >
              <input
                type="text"
                placeholder="Username"
                value={form.username}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, username: e.target.value }))
                }
                className="h-10 rounded-md border border-[#EEEEEE] px-2.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#222]"
              />
              <input
                type="text"
                placeholder="Full name"
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                className="h-10 rounded-md border border-[#EEEEEE] px-2.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#222]"
              />
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, email: e.target.value }))
                }
                className="h-10 rounded-md border border-[#EEEEEE] px-2.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#222]"
              />
              <select
                value={form.role}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    role: e.target.value as AdminUserRole,
                  }))
                }
                className="h-10 rounded-md border border-[#EEEEEE] px-2.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#222]"
              >
                <option value="STAFF">STAFF</option>
                <option value="ADMIN">ADMIN</option>
              </select>
              <button
                type="submit"
                disabled={creating}
                className="h-10 rounded-md bg-[#222] px-4 text-[12px] font-semibold text-white hover:bg-[#333] disabled:opacity-50"
              >
                {creating ? "Creating..." : "Add User"}
              </button>
            </form>
          </section>

          <section className="rounded-lg border border-[#EEEEEE] bg-white p-4 sm:p-6">
            <h2 className="text-[16px] font-semibold text-[#222]">Users</h2>
            {!!error && (
              <div className="mt-3 rounded-md border border-[#FFD5D8] bg-[#FFF1F2] px-3 py-2 text-[13px] text-[#FB2C36]">
                {error}
              </div>
            )}
            {loading ? (
              <p className="mt-3 text-sm text-[#6D6D6D]">Loading users...</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[#EEEEEE] text-[12px] text-[#6D6D6D]">
                      <th className="px-2 py-2">Name</th>
                      <th className="px-2 py-2">Email</th>
                      <th className="px-2 py-2">Role</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2">Last login</th>
                      <th className="px-2 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map((user) => {
                      const busy = !!busyIds[user.id];
                      const isSuperAdmin = user.role === "SUPER_ADMIN";
                      return (
                        <tr key={user.id} className="border-b border-[#F3F3F3] text-[13px]">
                          <td className="px-2 py-3">
                            <div className="font-medium text-[#222]">{user.name}</div>
                            <div className="text-[11px] text-[#6D6D6D]">@{user.username}</div>
                          </td>
                          <td className="px-2 py-3 text-[#222]">{user.email}</td>
                          <td className="px-2 py-3 text-[#222]">{user.role}</td>
                          <td className="px-2 py-3">
                            <span
                              className={`inline-flex rounded-full px-2 py-1 text-[11px] font-medium ${
                                user.isActive
                                  ? "bg-[#ECFDF3] text-[#0F7A45]"
                                  : "bg-[#FFF1F2] text-[#B42318]"
                              }`}
                            >
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-2 py-3 text-[#222]">{formatDate(user.lastLogin)}</td>
                          <td className="px-2 py-3">
                            <div className="flex flex-wrap gap-2">
                              {!isSuperAdmin && (
                                <>
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() =>
                                      void withUserBusy(user.id, () =>
                                        updateUserStatus(user.id, !user.isActive)
                                      )
                                    }
                                    className="h-8 rounded-md border border-[#EEEEEE] px-2 text-[11px] font-medium text-[#222] hover:bg-[#F7F7F7] disabled:opacity-50"
                                  >
                                    {user.isActive ? "Deactivate" : "Activate"}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() =>
                                      void withUserBusy(user.id, () => resendSetupEmail(user.id))
                                    }
                                    className="h-8 rounded-md border border-[#EEEEEE] px-2 text-[11px] font-medium text-[#222] hover:bg-[#F7F7F7] disabled:opacity-50"
                                  >
                                    Resend Invite
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {sortedUsers.length === 0 ? (
                      <tr>
                        <td className="px-2 py-4 text-[13px] text-[#6D6D6D]" colSpan={6}>
                          No users found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </main>
    </DashboardLayout>
  );
}
